/**
 * @module app.cd
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import * as CD from 'app/cd/models/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openAuthSummary } from 'app/cd/components/summary/auth/auth-summary'; //CR change to CD - ESS-3444
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { AuthService, IAuthSearchFilters } from 'app/cd/services/auth-service';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { debounce } from 'app/shared/lib/decorators';

@autoinject()
export class MemberImportedAuths extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected member: CD.Member | null = null;
    protected importedAuths: ReadonlyArray<CD.Auth> = [];

    @observable
    protected filterText: string = '';
    protected authSearchFilters: IAuthSearchFilters = {}
    protected minSearchLength: number = 3;
    protected maxSearchLength: number = 200;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected openImportedAuthSummary = openAuthSummary;
    protected showAuthSummaryLink: boolean = false;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected dateServiceRangeDisplay?: string | null;

    protected importedAuthDetails: Record<string, boolean> = {};

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<CD.Auth[]>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly authService: AuthService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'authorizedDate',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        //never async.
        this.checkPermissions();
        this.loadCustomFieldTargets();
        this.loadImportedAuths();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(!this.memberId) { return; }
        // Close any current expanded details
        this.$table.closeAll();
        this.importedAuthDetails = {};
        if(this.authSearchFilters.detailFilter) {
            await this.searchImportedAuths();
            // expand all details when filter is used
            this.importedAuths.forEach(a => this.importedAuthDetails[a.id!] = true);
            this.$table.openAll();
        } else {
            await this.loadImportedAuths();
        }
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    @debounce(600)
    protected async filterTextChanged(): Promise<void> {
        const search = this.filterText;
        this.importedAuthDetails = {};

        // make sure minimum search length criteria is met
        if(search && search.length >= this.minSearchLength) {
            await this.searchImportedAuths();
            // expand all details when filter is used
            this.importedAuths.forEach(a => this.importedAuthDetails[a.id!] = true);
            this.$table.openAll();
        }
        // otherwise load unfiltered results and clear any potential previous filters
        else if(!search && this.authSearchFilters.detailFilter) {
            this.clearResults();
            this.authSearchFilters.detailFilter = null;
            await this.loadImportedAuths();
        }
    }

    protected importedAuthDetailLoaded(event: ExpandEvent<CD.Auth>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.importedAuthDetails[item.id!] = true;
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('cd.auth.summary');
    }

    protected clearResults() {
        this.importedAuths = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Auth');
    }

    @apiCall
    private async loadImportedAuths(): Promise<void> {
        if(!this.memberId) {
            return;
        }

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes: string[] = [
            'priority',
            'status',
            'payStatus',
            'placeOfService',
            'member',
            'member.plan.lineofbusiness',
            'member.plan.healthplan',
            'metadata.created.byuser',
            'metadata.modified.byuser'
        ];

        const fields = [
            'authorizedDate',
            'custom.lookup.*',
            'custom.text.*',
            'expirationDate',
            'member.plan.healthPlan.description',
            'member.plan.lineOfBusiness.description',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'metadata.modified.timestamp',
            'metadata.modified.byUser.description',
            'number',
            'payStatus.description',
            'placeOfService.description',
            'priority.description',
            'requestedDate',
            'status.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberImportedAuths(this.memberId, options, includes, fields);
            this.importedAuths = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async searchImportedAuths(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes: string[] = [
            'priority',
            'status',
            'payStatus',
            'placeOfService',
            'member',
            'member.plan.lineofbusiness',
            'member.plan.healthplan',
            'metadata.created.byuser',
            'metadata.modified.byuser'
        ];

        const fields = [
            'authorizedDate',
            'custom.lookup.*',
            'custom.text.*',
            'expirationDate',
            'member.plan.healthPlan.description',
            'member.plan.lineOfBusiness.description',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'metadata.modified.timestamp',
            'metadata.modified.byUser.description',
            'number',
            'payStatus.description',
            'placeOfService.description',
            'priority.description',
            'requestedDate',
            'status.description'
        ];

        this.authSearchFilters.memberId = this.memberId
        this.authSearchFilters.detailFilter = this.filterText

        try {
            this.state.beginLoad();
            const response = await this.authService.searchImportedAuths(this.authSearchFilters, options, includes, fields);
            this.importedAuths = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
